import { useId, useState } from "react";
import type { BuildingDto, FloorDto } from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { IconChevronDown, IconChevronUp } from "../../app/icons";

/** Korte naam van een verdieping voor knoppen en badges: de afkorting (BG, 1, K) of het begin van de naam. */
export const floorShort = (f: Pick<FloorDto, "level" | "name">) => f.level || f.name.slice(0, 2);

type Counts = ReadonlyMap<string, number>;

/**
 * Plek boven de lijsten: verdieping en ruimte. Blijft staan als je tussen Bevindingen, Assets,
 * Arbeidsmiddelen, Meters en Onderhoud en keuringen wisselt (de keuze hoort bij het gebouw, niet bij de lijst).
 */
export function PlaceBar({
  building: b,
  floorId,
  roomId,
  onFloor,
  onRoom,
  open,
  empty,
}: {
  building: BuildingDto;
  /** Gekozen verdieping, of null voor alle verdiepingen. */
  floorId: string | null;
  roomId: string | null;
  onFloor: (id: string | null) => void;
  /** Ruimte kiezen zet ook de verdieping van die ruimte. */
  onRoom: (id: string | null) => void;
  /** Open bevindingen per verdieping en per ruimte (id → aantal). */
  open: Counts;
  /** Niets in de lijst op deze plek. */
  empty?: boolean;
}) {
  const [pick, setPick] = useState<"floor" | "room" | null>(null);
  const pickId = useId();
  const floor = b.floors.find((f) => f.id === floorId);
  const room = b.rooms.find((r) => r.id === roomId);
  const active = Boolean(floor || room);
  return (
    <div className="placebar" role="group" aria-label="Plek">
      {b.floors.length > 0 && (
        <button
          type="button"
          className={`btn small placebtn ${floor ? "on" : ""}`}
          aria-haspopup="dialog"
          aria-expanded={pick === "floor"}
          aria-controls={pick === "floor" ? pickId : undefined}
          aria-label={`Verdieping: ${floor?.name ?? "alle verdiepingen"}. Andere verdieping kiezen`}
          onClick={() => setPick("floor")}
        >
          <span className="ellipsis">{floor?.name ?? "Alle verdiepingen"}</span>
          <IconChevronDown size={16} aria-hidden="true" />
        </button>
      )}
      {b.rooms.length > 0 && (
        <button
          type="button"
          className={`btn small placebtn ${room ? "on" : ""}`}
          aria-haspopup="dialog"
          aria-expanded={pick === "room"}
          aria-controls={pick === "room" ? pickId : undefined}
          aria-label={`Ruimte: ${room?.name ?? "alle ruimtes"}. Andere ruimte kiezen`}
          onClick={() => setPick("room")}
        >
          <span className="ellipsis">{room?.name ?? "Alle ruimtes"}</span>
          <IconChevronDown size={16} aria-hidden="true" />
        </button>
      )}
      {active && (
        <button type="button" className="linkbtn" onClick={() => onFloor(null)}>
          Wissen
        </button>
      )}
      {active && empty && <span className="muted small">Niets gevonden op deze plek.</span>}
      {pick && (
        <PlacePicker
          id={pickId}
          building={b}
          mode={pick}
          floorId={floorId}
          roomId={roomId}
          open={open}
          onPick={(id) => {
            setPick(null);
            if (pick === "floor") onFloor(id);
            else onRoom(id);
          }}
          onClose={() => setPick(null)}
        />
      )}
    </div>
  );
}

/**
 * Kiezer voor een verdieping of ruimte, ook voor lange lijsten (10+ verdiepingen): hoogste verdieping bovenaan,
 * met het aantal open bevindingen. Bij ruimtes over alle verdiepingen een zoekveld.
 */
export function PlacePicker({
  id,
  building: b,
  mode,
  floorId,
  roomId,
  open,
  withAll = true,
  onPick,
  onClose,
}: {
  id?: string;
  building: BuildingDto;
  mode: "floor" | "room";
  floorId: string | null;
  roomId: string | null;
  open: Counts;
  /** "Alle verdiepingen" / "Alle ruimtes" als eerste keuze (niet bij de tekening). */
  withAll?: boolean;
  onPick: (id: string | null) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const top = [...b.floors].reverse();
  const floorsForRooms = floorId ? top.filter((f) => f.id === floorId) : top;
  const t = q.trim().toLowerCase();
  const rooms = floorsForRooms.flatMap((f) =>
    b.rooms
      .filter((r) => r.floorId === f.id && (!t || `${r.name} ${f.name}`.toLowerCase().includes(t)))
      .map((r) => ({ r, f })),
  );
  const search = mode === "room" && !floorId && b.rooms.length > 8;
  const n = (k: string) => open.get(k) ?? 0;
  return (
    <Modal id={id} title={mode === "floor" ? "Verdieping" : "Ruimte"} onClose={onClose}>
      {search && (
        <input
          type="search"
          className="picksearch"
          placeholder="Zoek een ruimte"
          aria-label="Zoek een ruimte"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      )}
      <ul className="picklist">
        {mode === "floor" ? (
          <>
            {withAll && (
              <Row
                on={!floorId}
                label="Alle verdiepingen"
                sub={`${b.floors.length} verdiepingen`}
                count={0}
                onClick={() => onPick(null)}
              />
            )}
            {top.map((f) => {
              const k = b.rooms.filter((r) => r.floorId === f.id).length;
              return (
                <Row
                  key={f.id}
                  on={f.id === floorId}
                  short={floorShort(f)}
                  label={f.name}
                  sub={`${k} ${k === 1 ? "ruimte" : "ruimtes"}`}
                  count={n(f.id)}
                  onClick={() => onPick(f.id)}
                />
              );
            })}
          </>
        ) : (
          <>
            {withAll && (
              <Row
                on={!roomId}
                label="Alle ruimtes"
                sub={floorId ? `Op ${floorsForRooms[0]?.name.toLowerCase() ?? ""}` : "Op alle verdiepingen"}
                count={0}
                onClick={() => onPick(null)}
              />
            )}
            {rooms.map(({ r, f }) => (
              <Row
                key={r.id}
                on={r.id === roomId}
                short={floorShort(f)}
                label={r.name}
                sub={f.name}
                count={n(r.id)}
                onClick={() => onPick(r.id)}
              />
            ))}
            {t && !rooms.length && <li className="muted small">Geen ruimte gevonden.</li>}
          </>
        )}
      </ul>
    </Modal>
  );
}

function Row({
  on,
  short,
  label,
  sub,
  count,
  onClick,
}: {
  on: boolean;
  short?: string;
  label: string;
  sub: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <li>
      <button type="button" className="pickrow" aria-pressed={on} onClick={onClick}>
        {short !== undefined && <span className="lvl">{short}</span>}
        <span className="grow">
          <strong>{label}</strong>
          <span className="muted small block">{sub}</span>
        </span>
        {count > 0 && <span className="pickopen small">{count} open</span>}
      </button>
    </li>
  );
}

/**
 * Liftpaneel bij de tekening. Desktop: een kolom met alle verdiepingen (hoogste bovenaan) en het aantal open
 * bevindingen. Telefoon: omlaag, de huidige verdieping (opent de kiezer) en omhoog.
 */
export function FloorLift({
  building: b,
  floorId,
  onFloor,
  open,
  phone,
}: {
  building: BuildingDto;
  floorId: string;
  onFloor: (id: string) => void;
  open: Counts;
  phone: boolean;
}) {
  const [pick, setPick] = useState(false);
  const pickId = useId();
  const k = b.floors.findIndex((f) => f.id === floorId);
  const cur = b.floors[k];
  const n = (id: string) => open.get(id) ?? 0;
  const label = (f: FloorDto) => `${f.name}${n(f.id) ? `, ${n(f.id)} open` : ""}`;
  if (b.floors.length < 2 || !cur) return null;
  if (!phone)
    return (
      <div className="lift" role="group" aria-label="Verdiepingen">
        {[...b.floors].reverse().map((f) => (
          <button
            key={f.id}
            type="button"
            aria-pressed={f.id === floorId}
            aria-label={label(f)}
            title={label(f)}
            onClick={() => onFloor(f.id)}
          >
            {floorShort(f)}
            {n(f.id) > 0 && (
              <span className="liftn" aria-hidden="true">
                {n(f.id)}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  const step = (d: number) => {
    const next = b.floors[k + d];
    if (next) onFloor(next.id);
  };
  return (
    <div className="liftbar" role="group" aria-label="Verdiepingen">
      <button
        type="button"
        className="btn small ghost ico"
        aria-label="Verdieping lager"
        disabled={k <= 0}
        onClick={() => step(-1)}
      >
        <IconChevronDown size={20} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="liftcur"
        aria-haspopup="dialog"
        aria-expanded={pick}
        aria-controls={pick ? pickId : undefined}
        aria-label={`Verdieping: ${label(cur)}. Andere verdieping kiezen`}
        onClick={() => setPick(true)}
      >
        <span className="lvl">{floorShort(cur)}</span>
        <span className="grow ellipsis">{cur.name}</span>
        {n(cur.id) > 0 && <span className="pickopen small">{n(cur.id)} open</span>}
      </button>
      <button
        type="button"
        className="btn small ghost ico"
        aria-label="Verdieping hoger"
        disabled={k >= b.floors.length - 1}
        onClick={() => step(1)}
      >
        <IconChevronUp size={20} aria-hidden="true" />
      </button>
      {pick && (
        <PlacePicker
          id={pickId}
          building={b}
          mode="floor"
          floorId={floorId}
          roomId={null}
          open={open}
          withAll={false}
          onPick={(id) => {
            setPick(false);
            if (id) onFloor(id);
          }}
          onClose={() => setPick(false)}
        />
      )}
    </div>
  );
}
